import React, { useEffect, useRef, useState } from "react";

// Small disclaimer chip placed near user-facing AI features. Click
// (or focus) the chip to expand a short popover explaining that we're
// still training the AI on the CLA, so output may be inconsistent.
//
// Variants:
//   pill   → uppercase chip suitable for section/modal headers (default)
//   inline → smaller mixed-case chip, fits next to a button or label
const RED = "#E06060";
const RED_BG = "#FDECEC";
const RED_DEEP = "#9A2A2A";

const EXPANDED_COPY = "We're training our AI coach with the CLA - real learning takes time! Thanks for being patient.";

export function TrainingBadge({ variant = "pill", style }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isInline = variant === "inline";

  const buttonStyle = isInline
    ? {
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 10.5,
        fontWeight: 800,
        color: "#fff",
        padding: "2px 9px",
        borderRadius: 6,
        background: RED,
        border: `1px solid ${RED}`,
        letterSpacing: 0.3,
        lineHeight: 1.3,
        fontFamily: "inherit",
        whiteSpace: "nowrap",
        boxShadow: `0 0 0 2px ${RED}22`,
        cursor: "pointer",
      }
    : {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11,
        fontWeight: 800,
        color: "#fff",
        padding: "3px 11px",
        borderRadius: 999,
        background: RED,
        border: `1px solid ${RED}`,
        letterSpacing: 0.2,
        lineHeight: 1.3,
        fontFamily: "inherit",
        whiteSpace: "nowrap",
        boxShadow: `0 0 0 2px ${RED}22`,
        cursor: "pointer",
      };

  return (
    <span ref={wrapRef} style={{ position: "relative", display: "inline-block", ...style }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        aria-expanded={open}
        aria-label="About this AI feature"
        style={buttonStyle}
      >
        <span style={{ width: isInline ? 5 : 6, height: isInline ? 5 : 6, borderRadius: "50%", background: "#fff", opacity: 0.9 }} />
        Still in Training
      </button>
      {open && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 9999,
            minWidth: 220,
            maxWidth: 280,
            padding: "10px 12px",
            background: RED_BG,
            color: RED_DEEP,
            border: `1px solid ${RED}`,
            borderRadius: 10,
            fontSize: 12,
            lineHeight: 1.45,
            fontWeight: 600,
            fontFamily: "inherit",
            textTransform: "none",
            letterSpacing: 0,
            whiteSpace: "normal",
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
          }}
        >
          {EXPANDED_COPY}
        </span>
      )}
    </span>
  );
}

export default TrainingBadge;
